// Execute and the endpoint Test call on the Lender Hub API (LH-B3). The API walks the canvas itself: Execute in the
// batch's sandbox on saved replies (nothing recorded), the Test call on the runner's UAT container (recorded in the
// run log, codes captured, the error-rate rule applied). Their answers come back in the shapes the screens already
// show for the in-browser engine (src/lib/flowEngine.ts ExecResult, src/lib/testCall.ts TestCallResult).
import type { ExecResult, ExecStep } from '@/lib/flowEngine'
import type { TestCallResult } from '@/lib/testCall'
import { api } from '@/lib/hubApi'
import { whenSynced } from '@/store/hubStore'
import type { RunStep, StandardAnswer, WorkflowRun, WorkflowVersion } from '@/types'

const enc = encodeURIComponent

interface HubExecute { answer: StandardAnswer; steps: RunStep[]; lit: string[]; ms: number; trace: ExecStep[]; answerNode: string | null; stoppedBy: ExecResult['stoppedBy']; waitedS: number }
interface HubTestCall { request: TestCallResult['request']; httpStatus: number; response: unknown; run: WorkflowRun | null; trace?: ExecStep[]; ms?: number; fresh?: string[]; ranOn?: string }

/** Execute workflow on the Hub: the drawing on the canvas (saved or not) on the chosen replies. */
export async function executeOnHub(flowId: string, input: { applicant: Record<string, unknown>; replies: Record<string, string>; draft: Pick<WorkflowVersion, 'nodes' | 'edges' | 'settings' | 'batch'>; environment?: 'uat' | 'production' }): Promise<ExecResult> {
  // the workflow (and the batch it uses) may still be on its way to the Hub
  await whenSynced()
  const r = await api<HubExecute>('POST', `/workflows/${enc(flowId)}/execute`, { applicant: input.applicant, environment: input.environment ?? 'uat', replies: input.replies, draft: input.draft })
  return { steps: r.trace, answer: r.answer, answerNode: r.answerNode, codes: [], ms: r.ms, waitedS: r.waitedS, stoppedBy: r.stoppedBy }
}

/** The endpoint's Test call on the Hub (UAT). `fresh` = new lender codes it raised an alert for; `ranOn` = where it ran. */
export async function testCallOnHub(endpointId: string, applicant: Record<string, unknown>, replies: Record<string, string>): Promise<TestCallResult & { fresh: string[]; ranOn: string | null }> {
  await whenSynced()
  const r = await api<HubTestCall>('POST', `/endpoints/${enc(endpointId)}/test-call`, { applicant, replies })
  const answer = r.response as StandardAnswer
  return {
    httpStatus: r.httpStatus,
    request: r.request,
    response: r.response,
    result: r.trace ? { steps: r.trace, answer, answerNode: null, codes: [], ms: r.ms ?? 0, waitedS: 0, stoppedBy: 'answer' } : null,
    run: r.run,
    fresh: r.fresh ?? [],
    ranOn: r.ranOn ?? null,
  }
}
